import type { CSSProperties } from "react";
import { themeVar as colors, radius } from "../design-tokens";
import { useUsage } from "../lib/useUsage";
import { formatDay } from "../lib/usersApi";
import {
  atUsageLimit,
  isCreditsMessage,
  OUT_OF_CREDITS_URL,
  resetDateShort,
  usageTone,
  type Usage,
  type UsageTone,
} from "../lib/usageApi";

/**
 * client/src/ui/UsageWidgets.tsx — the monthly-report-limit surfaces (§6), board 1 and board 2 of
 * `_cowork_scratch/limit_design/{1-counter-placements,2-at-the-limit}.html`, translated into this
 * app's own tokens and components rather than the mockups' raw markup.
 *
 * EVERY DISPLAY COMPONENT IS SPLIT IN TWO: a `*View` that takes `usage: Usage` as a prop (pure,
 * trivially testable with a fixture, no network) and a plain-named container that calls
 * `useUsage()` and renders the view once data has arrived. The Users-screen panel and the wizard
 * gates import `useUsage()`/`atUsageLimit` directly because they already need the raw value to
 * decide whether to disable a button — the views below are for the three read-only surfaces
 * (A, B, C) that only ever DISPLAY it.
 *
 * THE THREE STATES, AND WHY THE "AT LIMIT" ONE IS NOT THE ALARM COLOR. The owner's spec: green
 * under 80%, amber from 80%, and "neutral dark grey" at the limit — explicitly NOT red. This
 * app's `caution`/`cautionBg` tokens are reserved for things that are actually wrong (a concession
 * that looks mis-keyed, a build that halted); running out of this month's reports is an expected,
 * unalarming event, so the limit state below reaches for `textPrimary`/`neutralChip`/`border`
 * instead — calm, not an error.
 */

interface TonePalette {
  fg: string;
  bg: string;
  line: string;
  track: string;
  fill: string;
}

const TONE_PALETTE: Record<UsageTone, TonePalette> = {
  normal: { fg: colors.verified, bg: colors.verifiedBg, line: colors.verifiedLine, track: colors.verifiedLine, fill: colors.verified },
  warn: { fg: colors.amber, bg: colors.amberBg, line: colors.amberLine, track: colors.amberLine, fill: colors.amber },
  limit: { fg: colors.textPrimary, bg: colors.neutralChip, line: colors.border, track: colors.border, fill: colors.textPrimary },
};

/** "6 of 10 reports" / tail text — the SAME three sentences the pill, and the words the tile and
 * the in-flow line build on, so a change to the wording changes every surface at once. */
function tailText(usage: Usage, tone: UsageTone): string {
  if (tone === "limit") return `Resets ${resetDateShort(usage.resets_on)}`;
  if (tone === "warn") return `${usage.remaining} left`;
  return `${usage.percent ?? 0}%`;
}

/* ------------------------------------------------------------------------ *
 * A — the top-bar pill                                                      *
 * ------------------------------------------------------------------------ */

export function UsagePillView({ usage }: { usage: Usage }) {
  if (usage.limit === null) return null; // nothing to meter for an account that is never limited
  const tone = usageTone(usage);
  const palette = TONE_PALETTE[tone];
  const pct = Math.max(0, Math.min(100, usage.percent ?? 0));

  return (
    <a
      href="/app#usage"
      data-testid="usage-pill"
      onClick={(e) => {
        // SCROLLS WHEN ALREADY ON HOME, rather than reloading the page out from under the agent.
        // `/home` does not exist in this app — the signed-in front door is `/app` (see
        // `home/AppHome.tsx`'s own docstring) — so this link targets the real route rather than
        // the mockup's literal address, which would otherwise be a dead link.
        if (typeof window !== "undefined" && window.location.pathname === "/app") {
          e.preventDefault();
          document.getElementById("usage")?.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }}
      aria-label={`${usage.used} of ${usage.limit} reports used this month, ${tailText(usage, tone)}`}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 10,
        flex: "none",
        whiteSpace: "nowrap",
        padding: "6px 12px",
        borderRadius: 999,
        textDecoration: "none",
        fontSize: 13,
        fontWeight: 600,
        border: `1px solid ${palette.line}`,
        background: palette.bg,
        color: palette.fg,
      }}
    >
      <span>
        {usage.used} of {usage.limit} reports
      </span>
      <span
        aria-hidden="true"
        style={{ width: 56, height: 6, borderRadius: 999, background: palette.track, display: "block", overflow: "hidden" }}
      >
        <span style={{ display: "block", width: `${pct}%`, height: 6, background: palette.fill }} />
      </span>
      <span style={{ fontWeight: 500 }}>{tailText(usage, tone)}</span>
    </a>
  );
}

export function UsagePill() {
  const { usage } = useUsage();
  if (!usage) return null;
  return <UsagePillView usage={usage} />;
}

/* ------------------------------------------------------------------------ *
 * B — the Home tile                                                         *
 * ------------------------------------------------------------------------ */

function tileLine(usage: Usage, tone: UsageTone): string {
  if (tone === "limit") {
    return "You've used this month's reports. You can still open and read every brief. " +
      `Importing and pulling comps resumes ${resetDateShort(usage.resets_on)}, or ask your admin for more.`;
  }
  if (tone === "warn") {
    const n = usage.remaining ?? 0;
    return n === 1
      ? "1 report left this month. A redo uses one too."
      : `${n} reports left this month. A redo uses one too.`;
  }
  const n = usage.remaining ?? 0;
  return `${n} left. Every Generate counts, redos included. Resets ${resetDateShort(usage.resets_on)}.`;
}

function monthName(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return "";
  // `resets_on` is the FIRST of next month, so "this month" is the one before it.
  const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  const resetMonthIdx = Number(m[2]) - 1; // 0-based
  const thisMonthIdx = (resetMonthIdx + 11) % 12;
  return months[thisMonthIdx] ?? "";
}

export function UsageTileView({ usage }: { usage: Usage }) {
  if (usage.limit === null) {
    return (
      <section id="usage" aria-labelledby="vb-usage-heading" style={tileCardStyle(TONE_PALETTE.normal)}>
        <p id="vb-usage-heading" style={{ margin: 0, fontSize: 13, fontWeight: 500, color: colors.textSecondary }}>
          Reports this month
        </p>
        {usage.limit_source === "credits" ? (
          // Billing (dark): credits, not the monthly count, decide for this account.
          <p style={{ margin: 0, fontSize: 14, color: colors.textSecondary }}>
            Your briefs use the reports in your plan. You&apos;ve generated {usage.used} report
            {usage.used === 1 ? "" : "s"} this month. <a href="/billing/credits">See your plan and reports</a>
          </p>
        ) : (
          <p style={{ margin: 0, fontSize: 14, color: colors.textSecondary }}>
            Administrators are not limited. You&apos;ve generated {usage.used} report{usage.used === 1 ? "" : "s"} this
            month.
          </p>
        )}
      </section>
    );
  }

  const tone = usageTone(usage);
  const palette = TONE_PALETTE[tone];
  const pct = Math.max(0, Math.min(100, usage.percent ?? 0));

  return (
    <section id="usage" aria-labelledby="vb-usage-heading" style={tileCardStyle(palette)}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <span id="vb-usage-heading" style={{ fontSize: 13, fontWeight: 500, color: colors.textSecondary }}>
          Reports this month
        </span>
        <span style={{ fontSize: 13, color: colors.textSecondary }}>{monthName(usage.resets_on)}</span>
      </div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
        <span style={{ fontSize: 32, fontWeight: 700, letterSpacing: "-0.02em", color: colors.textPrimary }}>
          {usage.used}
        </span>
        <span style={{ fontSize: 15, color: colors.textSecondary }}>
          of {usage.limit} used · {pct}%
        </span>
      </div>
      <div style={{ height: 8, borderRadius: 999, background: palette.track, overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: 8, background: palette.fill }} />
      </div>
      <p style={{ margin: 0, fontSize: 13, color: tone === "normal" ? colors.textSecondary : palette.fg }}>
        {tileLine(usage, tone)}
      </p>
      <UsageHistory items={usage.items} />
    </section>
  );
}

function tileCardStyle(palette: TonePalette): CSSProperties {
  return {
    padding: "20px 22px",
    background: colors.bgSurface,
    border: `1px solid ${palette === TONE_PALETTE.normal ? colors.border : palette.line}`,
    borderRadius: radius.card,
    display: "flex",
    flexDirection: "column",
    gap: 10,
    marginBottom: 28,
  };
}

function UsageHistory({ items }: { items: Usage["items"] }) {
  if (items.length === 0) {
    return (
      <p style={{ margin: "4px 0 0", fontSize: 13, color: colors.textSecondary }}>
        No reports have counted against this month yet.
      </p>
    );
  }
  return (
    <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${colors.border}` }}>
      <h3
        style={{
          margin: "0 0 6px",
          fontSize: 11.5,
          fontWeight: 600,
          color: colors.textSecondary,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
        }}
      >
        This month
      </h3>
      <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 6 }}>
        {items.map((it, i) => (
          <li
            key={it.job_id ?? `${it.date}:${i}`}
            style={{ display: "flex", justifyContent: "space-between", gap: 12, fontSize: 13 }}
          >
            {/* `address` is null while a build using this slot is still running and has not named
                its subject yet — a calm note, never the literal word "null" and never a guessed
                address (report_limit.py: "never a placeholder"). */}
            <span style={{ color: it.address ? colors.textPrimary : colors.textSecondary }}>
              {it.address ?? "Address not yet known — still building"}
            </span>
            <span style={{ color: colors.textSecondary, whiteSpace: "nowrap" }}>
              {formatDay(it.date) ?? it.date} · {it.kind === "regenerate" ? "Redo" : "New"}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function UsageTile() {
  const { usage, loading, failed } = useUsage();
  // Says nothing while it does not know, and nothing on failure — the same rule `ServiceNotice`
  // (home/AppHome.tsx) follows: "we learned nothing, so we say nothing," never a zero that looks
  // like a real count.
  if (loading || failed || !usage) return null;
  return <UsageTileView usage={usage} />;
}

/* ------------------------------------------------------------------------ *
 * C — the in-flow line, above "Pull comps" / the subject-and-comps pull     *
 * and above "Generate"                                                      *
 * ------------------------------------------------------------------------ */

export type UsageLineAction = "generate" | "pull";

const ACTION_VERB: Record<UsageLineAction, string> = {
  generate: "Generating",
  pull: "Pulling records and comps",
};

export function usageLineText(usage: Usage, action: UsageLineAction): string {
  const tone = usageTone(usage);
  const verb = ACTION_VERB[action];
  if (tone === "limit") {
    return `${usage.used} of ${usage.limit} reports used this month. ${verb} is paused until ` +
      `${resetDateShort(usage.resets_on)}. Your admin can raise your limit.`;
  }
  const remaining = usage.remaining ?? 0;
  if (tone === "warn" && remaining <= 1) {
    return `This is your last report this month. ${verb} uses it.`;
  }
  return `${verb} uses 1 of your ${remaining} remaining report${remaining === 1 ? "" : "s"} this month.`;
}

export function UsageLineView({ usage, action }: { usage: Usage; action: UsageLineAction }) {
  if (usage.limit === null) return null; // an admin has nothing to be told here
  const tone = usageTone(usage);
  const palette = TONE_PALETTE[tone];
  return (
    <p
      data-testid={`usage-line-${action}`}
      style={{ margin: "0 0 10px", fontSize: 13, color: tone === "normal" ? colors.textSecondary : palette.fg }}
    >
      {usageLineText(usage, action)}
    </p>
  );
}

export function UsageLine({ action }: { action: UsageLineAction }) {
  const { usage, loading, failed } = useUsage();
  if (loading || failed || !usage) return null;
  return <UsageLineView usage={usage} action={action} />;
}

/* ------------------------------------------------------------------------ *
 * Board 2 — at the limit: the quiet banner and the per-control reason       *
 * ------------------------------------------------------------------------ */

/** The reason text that belongs BESIDE a disabled control, never instead of a disabled control
 * with no explanation (project rule: "no dead controls"). */
export function LimitNoticeView({ usage }: { usage: Usage }) {
  return (
    <p data-testid="limit-notice" role="status" style={{ margin: "6px 0 0", fontSize: 13, color: colors.textPrimary }}>
      Paused: {usage.used} of {usage.limit} reports used this month. Resumes {resetDateShort(usage.resets_on)}.
    </p>
  );
}

/** The quiet banner at the top of a step that has lost its import/pull/upload controls to the
 * limit. "Ask my admin for more" is TEXT here, not a link or a button: there is no in-app message
 * an agent's own screen can send to their admin, and a control that cannot act is the exact "dead
 * control" the project forbids — so the ask is stated rather than offered as something to click. */
export function UsageLimitBannerView({ usage }: { usage: Usage }) {
  return (
    <div
      data-testid="usage-limit-banner"
      role="status"
      style={{
        padding: "16px 20px",
        borderRadius: radius.card,
        border: `1px solid ${colors.border}`,
        background: colors.neutralChip,
        display: "flex",
        flexDirection: "column",
        gap: 4,
        marginBottom: 20,
      }}
    >
      <p style={{ margin: 0, fontSize: 15, fontWeight: 600, color: colors.textPrimary }}>
        You&apos;ve used all {usage.limit} reports for this month.
      </p>
      <p style={{ margin: 0, fontSize: 14, color: colors.textSecondary }}>
        Your address and notes are saved as a draft. Pulling records and comps resumes{" "}
        {resetDateShort(usage.resets_on)}. Every finished brief stays open to read and download. Ask your admin
        for more.
      </p>
    </div>
  );
}

/**
 * THE RACE CASE (lead directive, 2026-10-02): proactive disabling reads a round trip OLD. Two
 * tabs at 9 of 10 can both see an enabled button; the SERVER is what actually decides, and its
 * `429 limit_reached` is the real refusal — a click that gets one back and shows nothing is
 * exactly the dead control this project forbids. This renders the server's OWN sentence
 * (`report_limit.py`'s `message`, which already names the count and the reset date in prose —
 * nothing here recomputes or re-words it) in the same calm, non-alarm slot the proactive notices
 * use. `role="status"`, not `"alert"`: hitting the limit is an expected quota event, not a bug. */
export function ServerLimitNotice({ message }: { message: string }) {
  return (
    <p
      data-testid="server-limit-notice"
      role="status"
      style={{ margin: "6px 0 10px", fontSize: 13, color: colors.textPrimary, fontWeight: 600 }}
    >
      {message}
      {isCreditsMessage(message) && (
        <>
          {" "}
          <a href={OUT_OF_CREDITS_URL}>See your options</a>
        </>
      )}
    </p>
  );
}

export { atUsageLimit };
